import { ActivatedRoute, NavigationExtras } from '@angular/router';
import { SortOrderEnum } from '@shared/types';
import { Pagination } from '@ui-kit/miscs';
import { TableColumn } from '@ui-kit/table/_core';
import { difference, orderBy } from 'lodash-es';
import { TableOperationsHelper } from './table-operations.helper';

function paginationRouterOptions(route: ActivatedRoute, pagination: Pagination): NavigationExtras {
    return {
        relativeTo: route,
        queryParams: computePaginationQueryParams(pagination),
        queryParamsHandling: 'merge',
        replaceUrl: true,
    };
}

function searchRouterOptions(route: ActivatedRoute, searchValue: string, pagination: Pagination): NavigationExtras {
    return {
        relativeTo: route,
        queryParams: {
            search: searchValue || undefined,
            page: 1,
            size: pagination.take,
            view: undefined,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
    };
}

function columnsRouterOptions<T>(route: ActivatedRoute, columns: TableColumn<T>[]): NavigationExtras {
    const queryParams = { col: computeColumnsQueryParams(columns), view: undefined };

    return {
        relativeTo: route,
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
    };
}

function sortRouterOptions<T>(route: ActivatedRoute, columns: TableColumn<T>[]): NavigationExtras {
    const queryParams = { sB: computeSortQueryParams(columns), view: undefined };

    return {
        relativeTo: route,
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
    };
}

function resetViewRouterOptions<T>(
    route: ActivatedRoute,
    pagination: Pagination,
    columns: TableColumn<T>[]
): NavigationExtras {
    const paginationQP = computePaginationQueryParams(pagination);

    return {
        relativeTo: route,
        queryParams: {
            page: paginationQP.page,
            size: paginationQP.size,
            col: QueryParamsHelper.computeColumnsQueryParams(columns),
            sB: QueryParamsHelper.computeSortQueryParams(columns),
        },
        replaceUrl: true,
    };
}

function computePaginationQueryParams(pagination: Pagination): { page: number; size: number } {
    return { page: pagination.skip / pagination.take + 1, size: pagination.take };
}

function readPaginationQueryParams(params: Record<string, string>, options: number[]): { take: number; skip: number } {
    const rawSize = Number(params['size']);
    const rawPage = Number(params['page']);

    const isValidSize = options.includes(rawSize);
    const take = isValidSize ? rawSize : options[0];
    const skip = !isNaN(rawPage) && rawPage > 1 && take ? (rawPage - 1) * take : 0;

    return { take: take || 15, skip: skip || 0 };
}

function computeColumnsQueryParams<T>(columns: TableColumn<T>[]): string {
    return columns
        .filter(column => column.isVisible)
        .map(column => column.field)
        .join('-');
}

function computeSortQueryParams<T>(columns: TableColumn<T>[]): string[] {
    const sortingCriteria: { criterion: T; order: SortOrderEnum }[] = orderBy(columns, ['sortPriority'])
        .filter(c => c.isSortingActive)
        .map(c => {
            return {
                criterion: c.field,
                order: c.sortOrder,
            };
        });
    return sortingCriteria.map(sort => `${sort.criterion}-${sort.order}`);
}

function readSortingOptionsFromQueryParams<T>(
    columns: TableColumn<T>[],
    sortByOptions: string | any[],
    defaultColumns: TableColumn<T>[]
): TableColumn<T>[] {
    const sortBy = QueryParamsHelper.getQueryParamAsArray(sortByOptions);
    columns.forEach(c => {
        c.isSortingActive = false;
        c.sortOrder = SortOrderEnum.ASC;
        c.sortPriority = null;
    });

    sortBy
        .filter(sort => sort.indexOf('-') > -1)
        .slice(0, 5)
        .forEach((sort, index) => {
            const field = Number(sort.split('-')[0]);
            const order = Number(sort.split('-')[1]);

            if (!isNaN(field) && !isNaN(order)) {
                const column = columns.find(c => c.field === field);
                if (column) {
                    column.isSortingActive = true;
                    column.sortOrder = order;
                    column.sortPriority = index;
                }
            }
        });

    if (columns.filter(c => c.isSortingActive).length === 0) {
        columns.forEach(column => {
            const defaultColumn = defaultColumns.find(c => c.field === column.field);
            column.isSortingActive = defaultColumn?.isSortingActive || false;
            column.sortOrder = defaultColumn?.sortOrder || SortOrderEnum.ASC;
            column.sortPriority = null;
        });
    }

    return columns;
}

function formatDateForQueryParams(date: Date | null | undefined): string | undefined {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return undefined;
    }

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
}

function readDateRangeQueryParams(d1: string | null | undefined, d2: string | null | undefined): Date[] | undefined {
    return d1 && d2 ? TableOperationsHelper.computeDateRangeFromView([d1, d2]) : [];
}

function readColumnsQueryParams<T>(
    columns: string | null | undefined,
    operationsColumns: TableColumn<T>[],
    fallback: TableColumn<any>[]
): TableColumn<T>[] {
    if (!columns || (!columns.includes('-') && isNaN(Number(columns)))) {
        return fallback;
    }

    const columnsId = [...new Set(columns.split('-'))];
    const visibleColumns: TableColumn<T>[] = [];

    columnsId
        .filter((id: string) => !isNaN(Number(id)))
        .forEach((id: string, index: number) => {
            const column = fallback.find(el => el.field === Number(id));

            if (column) {
                column.isVisible = true;
                visibleColumns.push(column);
                if (operationsColumns[column?.field]) {
                    operationsColumns[column?.field]!.orderPriority = index;
                }
            }
        });

    const hiddenColumns = difference(fallback, visibleColumns) || [];
    hiddenColumns.forEach(column => {
        column.isVisible = false;
    });

    return visibleColumns.length === 0 ? fallback : [...visibleColumns, ...hiddenColumns];
}

function readFiltersQueryParams(data: string | null | undefined, options: any[]): any[] {
    const qp = QueryParamsHelper.getQueryParamAsArray(data);
    const result: any[] = [];
    qp.filter(id => !isNaN(Number(id))).forEach(id => {
        // eslint-disable-next-line eqeqeq -- type check skipped on purpose
        const r = options.find(v => v.id == id || v.value == id);
        if (r) {
            result.push(Number(id));
        }
    });

    return result;
}

function readViewQueryParam(view: string | null | undefined): number {
    return isNaN(Number(view)) ? -1 : Number(view);
}

function assignDateRangeToObj(
    obj: Record<string, any>,
    keyPrefix: string,
    range?: [Date | string | null, Date | string | null] | (Date | string | null)[]
) {
    if (range && range[0] && range[1]) {
        obj[`${keyPrefix}SD`] = range[0];
        obj[`${keyPrefix}ED`] = range[1];
    }
}

function getQueryParamAsArray(value: any): any[] {
    return Array.isArray(value) ? value : [value];
}

export const QueryParamsHelper = {
    paginationRouterOptions,
    searchRouterOptions,
    columnsRouterOptions,
    sortRouterOptions,
    resetViewRouterOptions,

    computePaginationQueryParams,
    computeColumnsQueryParams,
    computeSortQueryParams,

    readPaginationQueryParams,
    readSortingOptionsFromQueryParams,
    readDateRangeQueryParams,
    readViewQueryParam,
    readColumnsQueryParams,
    readFiltersQueryParams,

    formatDateForQueryParams,
    assignDateRangeToObj,
    getQueryParamAsArray,
};
